import { capitalize } from '../utils/string'
import OPTIONS from '../constants/settingOptions'
// 不用大写的单词们
const STOP_WORDS = {
    a: true,
    an: true,
    and: true,
    as: true,
    at: true,
    but: true,
    by: true,
    down: true,
    for: true,
    from: true,
    in: true,
    into: true,
    nor: true,
    of: true,
    on: true,
    onto: true,
    or: true,
    over: true,
    so: true,
    the: true,
    till: true,
    to: true,
    up: true,
    via: true,
    with: true,
    yet: true
}

// 杂志全称与缩写对照表
const JOURNAL_MAP = {
    'Bioorganic & Medicinal Chemistry Letters': 'Bioorg. Med. Chem. Lett.',
    'European Journal of Medicinal Chemistry': 'Eur. J. Med. Chem.',
    'Synthetic Communications': 'Synth. Commun.',
    'Chinese Chemical Letters': 'Chin. Chem. Lett.',
    'HETEROCYCLES': 'Heterocycles',
    'Heterocyclic Communications': 'Heterocyclic Commun.',
    'Journal of Molecular Liquids': 'J. Mol. Liq.',
    'Tetrahedron Letters': 'Tetrahedron Lett.',
    'Angew. Chem. Int. Ed. Engl.': 'Angew. Chem. Int. Ed.',
    'Asian Journal of Organic Chemistry': 'Asian J. Org. Chem.',
    'Chemical Biology & Drug Design': 'Chem. Biol. Drug. Des.',
    'Journal of Heterocyclic Chemistry': 'J. Heterocyclic Chem.',
}
export class Formatter {
    /**
     * Creates an instance of Formatter.
     * @param {object} options
     * @param {string} options.betweenFamilyAndGiven = ', ', // 姓名之间的符号
     * @param {string} options.betweenGivenName = ' ', // 名字第二个字与第三个字之间的符号（如果有）
     * @param {string} options.betweenAuthors = '; ', // 作者与作者之间的符号
     * @param {string} option.titleStyle = 'CAP_ALL_WORDS' // 标题样式，{ 'CAP_ALL_WORDS', 'CAP_FIRST_WORD' }
     * @param {boolean} options.includeTitle = true, // 是否包含标题
     * @param {boolean} options.useFullJournalName = false, // 是否使用杂志全称
     * @param {boolean} options.useFullGivenName = false, // 名字是否使用全称，而不是首字母缩写
     * @param {boolean} options.useFullFamilyName = true, // 姓氏是否使用全称，而不是首字母缩写
     *     } 
     * @memberof Formatter
     */
    constructor(options) {
        const {
            abbrevs,
            misc: {
                givenBeforeFamily = 0, // select 里出现 false 会报错
                etalThreshold = 20,
                useAndBetweenLastTwo = 0,
                titleStyle = OPTIONS.TITLE_STYLE.CAP_ALL_WORDS
            },
            conjunctions: {
                betweenFamilyAndGiven = ' ',
                betweenGivenName = ' ',
                betweenAuthors = '; '
            }
        } = options;
        this.betweenFamilyAndGiven = betweenFamilyAndGiven
        this.betweenGivenName = betweenGivenName
        this.betweenAuthors = betweenAuthors
        this.titleStyle = titleStyle
        this.useFullJournalName = !abbrevs.journal
        this.useFullGivenName = !abbrevs.givenName
        this.useFullFamilyName = !abbrevs.familyName
        this.etalThreshold = etalThreshold
        this.useAndBetweenLastTwo = useAndBetweenLastTwo
        this.givenBeforeFamily = givenBeforeFamily
        this.defaultInfo = {
            authors: '未找到作者信息，请自行补充',
            title: '未找到文章标题，请自行补充',
            journal: '未找到杂志名称，请自行补充',
            year: '未找到发行年份，请自行补充',
            volume: '未找到发行卷数，请自行补充',
            page: '未找到文章页码，请自行补充',
        }
    }

    /**
     * 
     * 
     * @param {string} title 
     * @returns 
     * @memberof Formatter
     */
    formatTitle(title) {
        if (!title) {
            return this.defaultInfo.title
        }
        const legitTitle = title.replace(//g, '-') // 把不能显示的化学键符号换成简单的短划线...
            .replace(/\n/g, '') // 换行符也去掉
        if (this.titleStyle === OPTIONS.TITLE_STYLE.CAP_FIRST_WORD) {
            return capitalize(legitTitle)
        }
        if (this.titleStyle === OPTIONS.TITLE_STYLE.CAP_ALL_WORDS) {
            return legitTitle.split(' ')
                .map(word => STOP_WORDS[word] ? word : capitalize(word))
                .join(' ')
        }
        return legitTitle
    }

    /**
     * 
     * 
     * @param {string} fullName 
     * @param {string} shortName 
     * @returns 
     * @memberof Formatter
     */
    formatJournal(fullName, shortName) {
        if (this.useFullJournalName) {
            return fullName || this.defaultInfo.journal
        }
        const hasNature = (str) => /nature/i.test(str);
        // Nature及其子刊，缩写之后不加点，特殊处理一下
        if (hasNature(fullName)) {
            return shortName
                .split(' ')
                .map(abbr => {
                    if (hasNature(abbr)) {
                        return abbr;
                    }
                    return `${abbr}.`
                })
                .join(' ')
        }
        return JOURNAL_MAP[fullName] || shortName || this.defaultInfo.journal
    }

    /**
     * 
     * 
     * @param {{given: string, family: string}[]} authors 
     * @returns {string}
     * @memberof Formatter
     */
    formatAuthors(authors) {
        if (!authors) {
            return this.defaultInfo.authors
        }
        if (authors.length > this.etalThreshold) {
            return this.formatAuthor(authors[0]) + 'et al.'
        }
        if (this.useAndBetweenLastTwo) {
            return authors.slice(0, -1)
                .map(author => this.formatAuthor(author))
                .join(this.betweenAuthors)
                + ' and '
                + this.formatAuthor(authors[authors.length - 1])
        }
        return authors
            .map(author => this.formatAuthor(author))
            .join(this.betweenAuthors)
    }

    /**
     * 
     * 
     * @param {object} author
     * @param {string} author.given
     * @param {string} author.family
     * @returns {string}
     * @memberof Formatter
     */
    formatAuthor(author) {
        const { given, family } = author;
        let givenName = given
        if (!this.useFullGivenName) {
            if (this.betweenGivenName.includes('-')) {
                // An-Xin -> A.-X.
                givenName = given.replace(/(.+?)(?:[ -]|$)/g, (match, $1) => {
                    return match.replace(/([^- ]+)/, (match, $1) => {
                        return $1[0].toUpperCase() + '.'
                    })
                })
            } else {
                givenName = given.split(/[ -]/)
                    .map(name => `${name[0].toUpperCase()}.`)
                    .join(this.betweenGivenName);
            }
        }
        const familyName = this.useFullFamilyName
            ? family
            : `${family[0].toUpperCase()}.`
        return this.givenBeforeFamily
            ? `${givenName}${this.betweenFamilyAndGiven}${familyName}`
            : `${familyName}${this.betweenFamilyAndGiven}${givenName}`
    }

    /**
     * 
     * 
     * @param {string} year 
     * @returns 
     * @memberof Formatter
     */
    formatYear(year) {
        return year || this.defaultInfo.year
    }

    /**
     * 
     * 
     * @param {string} volume 
     * @returns 
     * @memberof Formatter
     */
    formatVolume(volume) {
        return volume || this.defaultInfo.volume
    }

    /**
     * 
     * 
     * @param {string} page 
     * @returns 
     * @memberof Formatter
     */
    formatPage(page) {
        return page || this.defaultInfo.page
    }
}



// WEBPACK FOOTER //
// ./src/services/workFormatter.js